import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Input, IconButton, Chip, Stack, CircularProgress,
} from '@mui/joy';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { markdownComponents } from './markdownComponents';

const AGENT_DISPLAY = {
  research: { label: 'Research Agent', icon: '🔎' },
  outline: { label: 'Outline Agent', icon: '📋' },
  draft: { label: 'Draft Agent', icon: '📝' },
  revise: { label: 'Revise Agent', icon: '✏️' },
  finalize: { label: 'Finalize Agent', icon: '✅' },
  metadata: { label: 'Metadata Agent', icon: '🏷️' },
  chat: { label: 'Assistant', icon: '💬' },
};

/**
 * BlogChatPanel
 *
 * Free-form chat with stage action buttons for the AI blog authoring flow.
 * Every message — typed free text OR a stage button's predefined prompt — is classified
 * by the backend planner into one or more agents (research, outline, draft, revise,
 * finalize, metadata, chat). This panel shows which agent(s) handled each turn.
 */
export const BlogChatPanel = ({
  messages,
  isStreaming,
  error,
  plan, // last PlannerResult.intents array: [{agent, instructions, targets}, ...]
  activeAgent, // {agent, label} | null — currently executing agent
  onSend,
  onStop,
  onRetry,
  onStageAction,
  onProposeDraft, // called when a full draft is ready for diff
  disabled,
  researchCount = 0, // number of research sources captured so far (see Research tab for details)
}) => {
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    // Auto-scroll to bottom on new messages
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isStreaming]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    onSend(text);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const stageButtons = [
    { label: 'Research', stage: 'research', icon: <SearchIcon />, hint: 'Search for current info' },
    { label: 'Outline', stage: 'outline', icon: <FormatListBulletedIcon />, hint: 'Generate / refine outline' },
    { label: 'Draft', stage: 'draft', icon: <EditNoteIcon />, hint: 'Write full draft from outline' },
    { label: 'Revise', stage: 'revise', icon: <AutoFixHighIcon />, hint: 'Rewrite based on instructions' },
    { label: 'Finalize', stage: 'finalize', icon: <CheckCircleIcon />, hint: 'Polish full post for review' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 520,
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 2,
        background: 'rgba(255,255,255,0.02)',
        overflow: 'hidden',
      }}
    >
      {/* Header + Stage actions */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)' }}>
        <Typography level="title-md" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
          AI Writing Assistant
        </Typography>
        <Typography level="body3" sx={{ color: 'text.secondary', mb: 1.5 }}>
          Type anything — a planner routes your message to the right agent(s) automatically.
          Buttons below just send a ready-made prompt through the same planner.
          {researchCount > 0 ? ` ${researchCount} research source${researchCount === 1 ? '' : 's'} saved — see the Research tab.` : ''}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {stageButtons.map((b) => (
            <Button
              key={b.stage}
              size="sm"
              variant="outlined"
              startDecorator={b.icon}
              onClick={() => onStageAction(b.stage)}
              disabled={disabled || isStreaming}
              sx={{ borderColor: 'rgba(46,91,186,0.4)' }}
            >
              {b.label}
            </Button>
          ))}
        </Stack>
      </Box>

      {/* Messages */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          fontSize: '0.9rem',
        }}
      >
        {messages.length === 0 && (
          <Typography level="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Start by describing your topic, or click Research / Outline to begin.
          </Typography>
        )}

        {/* Routing indicator: shows which agent(s) the planner selected for the latest turn */}
        {isStreaming && activeAgent && (
          <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip size="sm" variant="soft" sx={{ background: 'rgba(46,91,186,0.15)', color: '#2E5BBA', fontWeight: 600 }}>
              {AGENT_DISPLAY[activeAgent.agent]?.icon || '🤖'} {AGENT_DISPLAY[activeAgent.agent]?.label || activeAgent.agent}
            </Chip>
            <Typography level="body3" sx={{ color: 'text.secondary' }}>{activeAgent.label}</Typography>
          </Box>
        )}
        {!isStreaming && Array.isArray(plan) && plan.length > 0 && (
          <Box sx={{ alignSelf: 'flex-start', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Typography level="body3" sx={{ color: 'text.secondary', mr: 0.5 }}>Routed to:</Typography>
            {plan.map((intent, i) => (
              <Chip key={i} size="sm" variant="outlined" sx={{ fontSize: '0.7rem' }}>
                {AGENT_DISPLAY[intent.agent]?.icon || '🤖'} {AGENT_DISPLAY[intent.agent]?.label || intent.agent}
              </Chip>
            ))}
          </Box>
        )}

        {messages.map((m, idx) => (
          <Box
            key={idx}
            sx={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '92%',
              background: m.role === 'user'
                ? 'rgba(46, 91, 186, 0.15)'
                : 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 2,
              px: 2,
              py: 1.25,
            }}
          >
            <Typography level="body3" sx={{ color: 'text.secondary', mb: 0.5, fontWeight: 600 }}>
              {m.role === 'user' ? 'You' : 'AI'}
            </Typography>

            {m.role === 'assistant' ? (
              (() => {
                const c = m.content || '';
                const looksLikeFullDraft = c.length > 800 || c.includes('---CHAT-SUMMARY---') || /\n##\s/.test(c);
                if (looksLikeFullDraft) {
                  return (
                    <Typography level="body3" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      Draft generated — see the <strong>Differences</strong> tab for the full article and unified diff.
                      {isStreaming ? ' Streaming…' : ''}
                    </Typography>
                  );
                }
                return (
                  <Box sx={{ '& p': { mb: 1 } }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {c || (isStreaming && idx === messages.length - 1 ? '…' : '')}
                    </ReactMarkdown>
                  </Box>
                );
              })()
            ) : (
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
            )}
          </Box>
        ))}

        {isStreaming && (
          <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <CircularProgress size="sm" /> <Typography level="body3">Writing…</Typography>
          </Box>
        )}

        {error && (
          <Typography level="body3" sx={{ color: '#ff6b6b' }}>
            {error}
          </Typography>
        )}
      </Box>

      {/* Input */}
      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.25)' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything, or use the stage buttons above…"
            disabled={isStreaming || disabled}
            sx={{ flex: 1, background: 'rgba(255,255,255,0.03)' }}
          />
          {isStreaming ? (
            <IconButton variant="solid" color="danger" onClick={onStop}>
              <StopIcon />
            </IconButton>
          ) : (
            <IconButton
              variant="solid"
              onClick={handleSend}
              disabled={!input.trim() || disabled}
              sx={{ background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)' }}
            >
              <SendIcon />
            </IconButton>
          )}
          <IconButton variant="outlined" onClick={onRetry} disabled={isStreaming || disabled || messages.length === 0}>
            <ReplayIcon />
          </IconButton>
        </Box>
        <Typography level="body3" sx={{ color: 'text.secondary', mt: 0.75 }}>
          Streaming enabled. Use Finalize to get a full polished draft + diff review.
        </Typography>
      </Box>
    </Box>
  );
};
